import { Injectable } from "@angular/core"
import { AngularFirestore } from "@angular/fire/firestore"
import { getCurrentTimestamp, showNotification } from "@tr/utils"
import * as firebase from "firebase/app"
import { ClaimedTestsService } from "./claimed-tests.service"
import { DataService } from "./data.service"

@Injectable({
  providedIn: "root",
})
export class BlockLinkedTestsService {
  public company: string
  public project: string
  public environment: string
  public testId: string
  public runId: string
  public platform: string
  public isPhysicalDevice: boolean
  public testType: string
  public reporterId = "A Parent or Linked Test Was Blocked or Failed"

  public incompleteId: string

  public numberToRefund: number
  public blockedCounter: number
  public db: any

  public blockedTests: any

  public blockLinkedChildrenAllPlatforms: boolean

  public children: any[]

  public osVersion: string

  public get uid(): string { return this.dataService.uid.value }
  public set uid(uid: string) { this.dataService.uid.next(uid) }

  constructor(
    private dataService: DataService,
    private afs: AngularFirestore,
    private claimedTestsService: ClaimedTestsService,
  ) {
    this.numberToRefund = 0
    this.blockedCounter = 0
    this.company = ""
    this.project = ""
    this.environment = ""
    this.testId = ""
    this.runId = ""
    this.platform = ""
    this.isPhysicalDevice = false
    this.incompleteId = ""
    this.testType = ""
    this.osVersion = ""

    this.blockedTests = []
    this.blockLinkedChildrenAllPlatforms = false

    this.db = firebase.firestore()
  }

  // this function is called directly from the manager's bugs approval component or test approval component
  public async blockChildren(
    testId,
    runId,
    platform,
    isPhysicalDevice,
    company,
    project,
    environment,
    testType,
    osVersion,
  ): Promise<void> {
    this.blockedTests = []
    this.company = company
    this.project = project
    this.environment = environment
    this.testId = testId
    this.runId = runId
    this.platform = platform
    this.isPhysicalDevice = isPhysicalDevice
    this.testType = testType
    this.osVersion = osVersion
    return this.afs
      .collection("companies")
      .doc(this.company)
      .collection("projects")
      .doc(this.project)
      .ref.get()
      .then(async doc => {
        const data = doc.data()
        if (data) {
          if (!data.autoBlockChildren) { return }
          this.blockLinkedChildrenAllPlatforms = data.blockLinkedChildrenAllPlatforms
          if (!this.blockLinkedChildrenAllPlatforms) {
            this.blockLinkedChildrenAllPlatforms = false
          }

          await this.getChildren(this.testId)
        }
      })
  }

  public async getChildren(testId): Promise<void> {
    await this.afs
      .collection("companies")
      .doc(this.company)
      .collection("projects")
      .doc(this.project)
      .collection("Test Cases")
      .ref.get()
      .then(async querySnapshot1 => {
        const children = []
        querySnapshot1.forEach(doc1 => {
          const data1 = doc1.data()
          if (data1.parents && data1.parents.includes(testId)) {
            children.push(data1.testID)
          }
        })
        if (this.blockLinkedChildrenAllPlatforms) {
          await this.afs
            .collection("IncompleteTests")
            .ref.where("company", "==", this.company)
            .where("project", "==", this.project)
            .where("runId", "==", this.runId)
            .get()
            .then(async querySnapshot => {
              querySnapshot.forEach(async doc => {
                const testData = doc.data()
                if (children.includes(testData.id)) {
                  if (this.runId === testData.runId) {
                    this.storeBlockedTests(testData)
                  }
                }
              })
              this.startNewBlockedTest()
            })
        } else {
          await this.afs
            .collection("IncompleteTests")
            .ref.where("runId", "==", this.runId)
            .where("platform", "==", this.platform)
            .where("company", "==", this.company)
            .where("project", "==", this.project)
            .where("osVersion", "==", this.osVersion)
            .get()
            .then(async querySnapshot => {
              querySnapshot.forEach(async doc => {
                const testData = doc.data()
                if (children.includes(testData.id)) {
                  if (
                    testData.platform === this.platform &&
                    testData.runId === this.runId
                  ) {
                    this.storeBlockedTests(testData)
                  }
                }
              })
              this.startNewBlockedTest()
            })
        }
      })
  }

  public storeBlockedTests(testData): void {
    const index = this.blockedTests.findIndex(
      x =>
        x.platform === testData.platform &&
        x.id === testData.id &&
        testData.isPhysicalDevice === x.isPhysicalDevice &&
        testData.osVersion === x.osVersion,
    )
    if (index === -1) {
      this.blockedTests.push(testData)
    }
  }

  public startNewBlockedTest(): void {
    if (this.blockedTests.length > 0) {
      const testData = this.blockedTests[0]
      this.blockedTests.splice(0, 1)
      this.checkAndBlockTest(testData)
    } else {
      this.refundCredits(this.numberToRefund)
    }
  }

  public async checkAndBlockTest(testData): Promise<void> {
    const input = "Parent Test was Blocked or Failed"
    if (testData.isApproved) {
      this.startNewBlockedTest()
    } else if (testData.isComplete) {
      this.loadTestInfoById(input, testData)
    } else if (testData.isClaimed) {
      this.loadTestInfoById(input, testData)
    } else {
      this.loadTestInfoById(input, testData)
    }
  }

  public loadTestInfoById(input, testData): void {
    let groupId = ""
    const groupIncompleteIds = []
    let groupStatus = "Incomplete"
    let isFinishedGroup = false
    const isFinishedGroupArray: boolean[] = []
    const groupStatusArray: string[] = []
    groupId = testData.groupId
    this.afs
      .collection("IncompleteTests")
      .ref.where("groupId", "==", testData.groupId)
      .get()
      .then(async querySnapshot => {
        querySnapshot.forEach(doc1 => {
          if (testData.groupId === doc1.data().groupId) {
            const docId = doc1.id
            const data1 = doc1.data()
            if (data1) {
              if (
                groupIncompleteIds.findIndex(x => x === data1.incompleteId) ===
                -1
              ) {
                groupIncompleteIds.push(docId)
              }
              if (
                data1.id === testData.id &&
                data1.company === testData.company &&
                data1.platform === testData.platform &&
                data1.runId === testData.runId &&
                data1.testType === testData.testType &&
                data1.isPhysicalDevice.toString() ===
                  testData.isPhysicalDevice.toString() &&
                data1.osVersion === testData.osVersion
              ) {
                isFinishedGroupArray.push(true)
                testData.incompleteId = docId
              } else {
                if (data1.isApproved) {
                  isFinishedGroupArray.push(true)
                } else {
                  isFinishedGroupArray.push(false)
                }
              }
              groupStatusArray.push(data1.status)
            }
            for (const groupStatusItem of groupStatusArray) {
              if (
                groupStatusItem === "Passed" &&
                groupStatus === "Unknown"
              ) {
                groupStatus = "Passed"
              } else if (
                groupStatusItem === "Blocked" &&
                groupStatus !== "Failed"
              ) {
                groupStatus = "Blocked"
              } else if (groupStatusItem === "Failed") {
                groupStatus = "Failed"
              }
            }
            isFinishedGroup = true
            for (const isFinishedGroupItem of isFinishedGroupArray) {
              if (!isFinishedGroupItem) {
                isFinishedGroup = false
              }
            }
          }
        })
        await this.markAsBlocked(input, testData, isFinishedGroup, groupStatus)
      })
  }

  public async markAsBlocked(input, testData, isFinishedGroup, groupStatus): Promise<void> {
    let hasUpdatedTest = false
    if (!testData.hasUpdatedTestCase) {
      const docRef = this.afs
        .collection("companies")
        .doc(testData.company)
        .collection("projects")
        .doc(testData.project)
        .collection("Test Cases")
        .doc(testData.id)
        .collection("Test Reports")
        .doc(testData.runId)
      docRef.ref.get().then(doc => {
        if (doc.exists) {
          let notes = []
          if (doc.data().testerNotes) {
            notes = doc.data().testerNotes
          }
          let device = ""
          if (testData.isPhysicalDevice) {
            device = " on a physical device"
          }
          if (testData.osVersion !== "" && testData.osVersion !== "Default") {
            device += " on OS Version " + testData.osVersion
          }
          notes.push(
            "Test " +
              testData.id +
              " was marked as blocked on " +
              testData.platform +
              device +
              " because: " +
              input,
          )
          this.db
            .collection("companies")
            .doc(this.company)
            .collection("projects")
            .doc(this.project)
            .collection("Test Cases")
            .doc(testData.id)
            .collection("Test Reports")
            .doc(testData.runId)
            .update({ testerNotes: notes })
        }
      })
      const finished = new Date()

      if (
        groupStatus === "Passed" ||
        groupStatus === "Unknown" ||
        groupStatus === "Incomplete"
      ) {
        this.db
          .collection("companies")
          .doc(this.company)
          .collection("projects")
          .doc(this.project)
          .collection("Test Cases")
          .doc(testData.id)
          .collection("Test Reports")
          .doc(testData.runId)
          .get()
          .then(doc => {
            if (!hasUpdatedTest) {
              let reporters = doc.data().reporters
              if (!reporters) {
                reporters = []
              }
              reporters.push({
                reporter: this.reporterId,
                status: "Failed",
                manager: this.uid,
                managerStatus: "Blocked",
                platform: testData.platform,
                osVersion: testData.osVersion,
              })
              this.db
                .collection("companies")
                .doc(this.company)
                .collection("projects")
                .doc(this.project)
                .collection("Test Cases")
                .doc(testData.id)
                .collection("Test Reports")
                .doc(testData.runId)
                .update({
                  reporters,
                  completed: finished,
                  status: "Blocked",
                  reporter: this.reporterId,
                })
            }
          })

        this.db
          .collection("companies")
          .doc(this.company)
          .collection("projects")
          .doc(this.project)
          .collection("Test Cases")
          .doc(testData.id)
          .update({
            lastTested: finished,
            lastRunId: testData.runId,
            status: "Blocked",
          })
      } else if (groupStatus === "Blocked") {
        this.db
          .collection("companies")
          .doc(this.company)
          .collection("projects")
          .doc(this.project)
          .collection("Test Cases")
          .doc(testData.id)
          .collection("Test Reports")
          .doc(testData.runId)
          .get()
          .then(doc => {
            if (!hasUpdatedTest) {
              let reporters = doc.data().reporters
              if (!reporters) {
                reporters = []
              }
              reporters.push({
                reporter: this.reporterId,
                status: "Blocked",
                manager: this.uid,
                managerStatus: "Blocked",
                platform: testData.platform,
                osVersion: testData.osVersion,
              })
              this.db
                .collection("companies")
                .doc(this.company)
                .collection("projects")
                .doc(this.project)
                .collection("Test Cases")
                .doc(testData.id)
                .collection("Test Reports")
                .doc(testData.runId)
                .update({
                  reporters,
                  completed: finished,
                  status: "Blocked",
                  reporter: this.reporterId,
                })
            }
          })
        this.db
          .collection("companies")
          .doc(this.company)
          .collection("projects")
          .doc(this.project)
          .collection("Test Cases")
          .doc(testData.id)
          .update({
            lastTested: finished,
            lastRunId: this.runId,
            status: "Blocked",
          })
      } else if (groupStatus === "Failed") {
        this.db
          .collection("companies")
          .doc(this.company)
          .collection("projects")
          .doc(this.project)
          .collection("Test Cases")
          .doc(testData.id)
          .collection("Test Reports")
          .doc(testData.runId)
          .get()
          .then(doc => {
            if (!hasUpdatedTest) {
              let reporters = doc.data().reporters
              if (!reporters) {
                reporters = []
              }
              reporters.push({
                reporter: this.reporterId,
                status: "Failed",
                manager: this.uid,
                managerStatus: "Blocked",
                platform: testData.platform,
                osVersion: testData.osVersion,
              })
              this.db
                .collection("companies")
                .doc(this.company)
                .collection("projects")
                .doc(this.project)
                .collection("Test Cases")
                .doc(testData.id)
                .collection("Test Reports")
                .doc(testData.runId)
                .update({
                  reporters,
                  completed: finished,
                  status: "Failed",
                })
            }
          })
        this.db
          .collection("companies")
          .doc(this.company)
          .collection("projects")
          .doc(this.project)
          .collection("Test Cases")
          .doc(testData.id)
          .update({
            lastTested: finished,
            lastRunId: testData.runId,
            status: "Failed",
          })
      }
      hasUpdatedTest = false
    }
    let hasUpdated = false

    if (isFinishedGroup) {
      const testRunsRef = await this.afs
        .collection("companies")
        .doc(this.company)
        .collection("projects")
        .doc(this.project)
        .collection("Test Runs")
        .doc(testData.runId)
        .ref.get()
      if (testRunsRef.exists) {
        const data1 = testRunsRef.data()
        if (!testData.hasUpdatedTestCase) {
          if (
            groupStatus === "Passed" ||
            groupStatus === "Unknown" ||
            groupStatus === "Incomplete"
          ) {
            const newCompletion =
              ((data1.passed + 1 + data1.blocked + data1.failed) /
                data1.totalTests) *
              100
            const blocked = data1.blocked + 1
            const incomplete = data1.incomplete - 1
            this.updateData(
              newCompletion,
              data1.passed,
              blocked,
              incomplete,
              data1.failed,
              testData,
            )
          } else if (groupStatus === "Blocked") {
            const newCompletion =
              ((data1.passed + 1 + data1.blocked + data1.failed) /
                data1.totalTests) *
              100
            const blocked = data1.blocked + 1
            const incomplete = data1.incomplete - 1
            this.updateData(
              newCompletion,
              data1.passed,
              blocked,
              incomplete,
              data1.failed,
              testData,
            )
          } else if (groupStatus === "Failed") {
            const newCompletion =
              ((data1.passed + data1.blocked + 1 + data1.failed) /
                data1.totalTests) *
              100
            const incomplete = data1.incomplete - 1
            const failed = data1.failed + 1
            this.updateData(
              newCompletion,
              data1.passed,
              data1.blocked,
              incomplete,
              failed,
              testData,
            )
          }
          hasUpdated = false
        }
      }
    } else {
      // TODO: Convert this to a promise and await for it...
      this.markIncompleteAsApproved("Blocked", testData)
    }

    // this.removeFromNeedsApproval(testData);
    if (isFinishedGroup) {
      this.removeIncompleteTest(testData)
    }
    const docRef1 = this.afs
      .collection("companies")
      .doc(this.company)
      .collection("projects")
      .doc(this.project)
      .collection("Test Runs")
      .doc(testData.runId)
    docRef1.ref.get().then(doc => {
      if (doc.exists) {
        let notes = []
        if (doc.data().testerNotes) {
          notes = doc.data().testerNotes
        }
        let device = ""
        if (testData.isPhysicalDevice) {
          device = " on a physical device"
        }
        if (testData.osVersion !== "" && testData.osVersion !== "Default") {
          device += " on OS Version " + testData.osVersion
        }
        notes.push(
          "Test " +
            testData.id +
            " was marked as blocked on " +
            testData.platform +
            device +
            " because: " +
            input,
        )
        this.db
          .collection("companies")
          .doc(this.company)
          .collection("projects")
          .doc(this.project)
          .collection("Test Runs")
          .doc(testData.runId)
          .update({ testerNotes: notes })
      }
    })

    this.removeFromClaimedTests(testData)
    this.removeFromNeedsApproval(testData)
    this.numberToRefund += testData.numberSteps - 1
    this.getChildren(testData.id)
  }

  public removeIncompleteTest(testData): void {
    this.deleteCollection(testData)
  }

  private async deleteCollection(testData): Promise<void> {
    const batch = this.db.batch()
    const qs = await this.db
      .collection("IncompleteTests")
      .where("groupId", "==", testData.groupId)
      .get()
    qs.forEach(doc => batch.delete(doc.ref))
    return batch.commit()
  }

  public updateData(newCompletion, passed, blocked, incomplete, failed, testData): void {
    if (!testData.hasUpdatedTestCase) {
      this.db
        .collection("companies")
        .doc(this.company)
        .collection("projects")
        .doc(this.project)
        .collection("Test Runs")
        .doc(testData.runId)
        .update({
          passed,
          blocked,
          failed,
          incomplete,
          resolvedTime: incomplete === 0 ? getCurrentTimestamp() : null,
          completionPercentage: newCompletion,
        })
      testData.hasUpdatedTestCase = true
    }
  }

  public markIncompleteAsApproved(status, testData): void {
    if (testData.incompleteId) {
      this.afs
        .collection("IncompleteTests")
        .doc(testData.incompleteId)
        .update({ isApproved: true, isClaimed: true, status: "Blocked" })
    }
  }

  public refundCredits(numberToRefund): void {
    if (numberToRefund <= 0) {
      return
    }
    const docRef = this.afs.collection("companies").doc(this.company)
    docRef.ref.get().then(doc => {
      if (doc.exists) {
        let creditsRemaining = doc.data().creditsRemaining
        creditsRemaining += numberToRefund
        this.afs
          .collection("companies")
          .doc(this.company)
          .update({ creditsRemaining })
      }
    })

    const date = new Date()
    const refund = true
    const credits = numberToRefund
    const amount = "NA"
    const token = {
      managerUid: this.uid,
      date,
      testCompany: this.company,
      testRun: this.runId,
      testId: this.testId,
      platform: this.platform,
      isPhysicalDevice: this.isPhysicalDevice,
      note: "Auto blocked tests credit refund",
    }
    const payment = {
      token,
      refund,
      credits,
      uid: this.uid,
      company: this.company,
    }
    const tempID =
      new Date() +
      " Amount: " +
      amount +
      " refund after auto block id: " +
      this.testId +
      this.platform +
      this.isPhysicalDevice +
      this.runId
    this.afs
      .collection("companies")
      .doc(this.company)
      .collection("transactions")
      .doc(tempID)
      .set(payment)
      .catch(error => {
        showNotification(
          "There was an error with the refund on auto blocking linked tests: " +
            error +
            " Please contact Support",
        )
      })
  }

  public removeFromNeedsApproval(testData): void {
    if (testData.randomId) {
      this.afs
        .collection("Managers")
        .doc("Needs Approval Bugs")
        .collection("Needs Approval")
        .ref.where("company", "==", this.company)
        .where("project", "==", this.project)
        .where("testEnvironment", "==", testData.environment)
        .where("testPlatform", "==", testData.platform)
        .where("testRun", "==", testData.runId)
        .where("testId", "==", testData.id)
        .where("failedStep", "==", testData.failedStep)
        .where("isPhysicalDevice", "==", testData.isPhysicalDevice)
        .where("randomId", "==", testData.randomId)
        .where("osVersion", "==", testData.osVersion)
        .get()
        .then()
        .then(querySnapshot1 => {
          querySnapshot1.forEach(doc => {
            const data = doc.data()
            data.incompleteId = doc.id
            const tempRunId = data.testRun
            const tempTestRunId = testData.runId
            if (
              data.company === testData.company &&
              tempRunId.toString() === tempTestRunId.toString() &&
              data.testId === testData.id &&
              data.testPlatform === testData.platform
            ) {
              this.afs
                .collection("Managers")
                .doc("Needs Approval Bugs")
                .collection("Needs Approval")
                .doc(data.incompleteId)
                .delete()
            }
          })
        })
    }
  }

  public removeFromClaimedTests(testData: any): void {
    this.afs
      .collection("Seva Testers")
      .ref.get()
      .then(querySnapshot => {
        querySnapshot.forEach(doc1 => {
          const data1 = doc1.id
          this.afs
            .collection("Seva Testers")
            .doc(data1)
            .collection("ClaimedTests")
            .ref.where("company", "==", testData.company)
            .where("project", "==", testData.project)
            .where("environment", "==", testData.environment)
            .where("platform", "==", testData.platform)
            .where("runId", "==", testData.runId)
            .where("testId", "==", testData.id)
            .where("isPhysicalDevice", "==", testData.isPhysicalDevice)
            .where("osVersion", "==", testData.osVersion)
            .get()
            .then(querySnapshot1 => {
              querySnapshot1.forEach(doc => {
                const data = doc.data()
                if (
                  data.platform === testData.platform &&
                  data.runId === testData.runId &&
                  data.testId === testData.id
                ) {
                  const claimedId = doc.id
                  this.afs
                    .collection("Seva Testers")
                    .doc(data1)
                    .collection("ClaimedTests")
                    .doc(this.claimedTestsService.getId(testData))
                    .delete()
                }
              })
            })
        })
      })
  }
}
